import React, { useEffect, useRef, useState } from 'react';
import { API } from '../../utils/api';
import WhiskeyLogo from './WhiskeyLogo';
import LucideIcon from '../LucideIcon';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const AGENT_BETA_THREAD_STORAGE_KEY = 'ws_agent_beta_thread_id';

function getOrCreateThreadId(): string {
  const existing = window.localStorage.getItem(AGENT_BETA_THREAD_STORAGE_KEY);
  if (existing) return existing;
  const created = (window.crypto as any)?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
  window.localStorage.setItem(AGENT_BETA_THREAD_STORAGE_KEY, created);
  return created;
}

/**
 * Internal-only test surface for the Milestone-2 LangGraph agent
 * (docs/build-plan/06-agent-framework-backend.md), reachable at /agent-beta
 * but not linked from anywhere in the app chrome. Deliberately minimal --
 * this is a beta-cohort test harness, not a product launch (the backend
 * route itself 404s for anyone not on AGENT_CHAT_BETA_USERS regardless of
 * whether this page loads). Not the eventual product surface: this is
 * scaffolding to prove the agent works end to end, not WhiskeyChat's
 * floating panel.
 *
 * The REACT_APP_AGENT_BETA_ENABLED flag is checked by the caller (App.tsx)
 * before this route is even registered -- not inside this component --
 * so there's no early return ahead of the hooks below (an early return
 * gated by the flag would violate React's rules of hooks by making the
 * hook count conditional).
 */
const AgentBetaChat: React.FC = () => {
  const [threadId] = useState(getOrCreateThreadId);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    API.get(`/agent-chat?threadId=${encodeURIComponent(threadId)}`)
      .then((res: any) => setMessages(Array.isArray(res?.messages) ? res.messages : []))
      .catch(() => {
        /* start with an empty conversation rather than blocking the page on a failed load */
      })
      .finally(() => setHistoryLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError('');
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setSending(true);
    try {
      const res: any = await API.post('/agent-chat', { message: text, threadId });
      setMessages((prev) => [...prev, { role: 'assistant', content: res.reply }]);
    } catch (e: any) {
      setError(e?.message || "Couldn't reach the agent. Try again.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', maxWidth: 640, margin: '0 auto' }}>
      <div style={{ padding: '16px 4px', borderBottom: '1px solid var(--border)' }}>
        <p className="ws-display" style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Agent beta (internal)</p>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-tertiary)' }}>
          Real LangGraph.js agent, one tool (get_current_lesson_context), DynamoDB-checkpointed. Thread: <code>{threadId.slice(0, 8)}</code>
        </p>
      </div>

      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {!historyLoaded && (
          <p className="ws-mono" style={{ margin: 'auto', fontSize: 12, color: 'var(--text-tertiary)' }}>Loading&hellip;</p>
        )}
        {historyLoaded && messages.length === 0 && (
          <div style={{ margin: 'auto', textAlign: 'center', padding: '0 12px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><WhiskeyLogo size={28} /></div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
              Try mentioning a lesson id, e.g. "what does basic-1 teach?"
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
              background: m.role === 'user' ? 'var(--ember)' : 'var(--surface-sunken)',
              color: m.role === 'user' ? 'white' : 'var(--text-primary)',
              border: m.role === 'user' ? 'none' : '1px solid var(--border)',
              borderRadius: 12,
              padding: '9px 12px',
              fontSize: 13,
              lineHeight: 1.55,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {m.content}
          </div>
        ))}
        {sending && (
          <div style={{ alignSelf: 'flex-start', background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: 12, padding: '9px 12px' }}>
            <span className="ws-mono" style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Thinking&hellip;</span>
          </div>
        )}
      </div>

      {error && <p role="alert" style={{ margin: '0 16px 8px', fontSize: 12, color: 'var(--danger, #b42318)' }}>{error}</p>}

      <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid var(--border)' }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message the agent…"
          rows={1}
          disabled={sending}
          style={{
            flex: 1, resize: 'none', fontSize: 13, lineHeight: 1.4, padding: '9px 12px', maxHeight: 80,
            color: 'var(--text-primary)', background: 'var(--surface-sunken)', border: '1px solid var(--border)',
            borderRadius: 10, outline: 'none', fontFamily: 'inherit',
          }}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!input.trim() || sending}
          aria-label="Send message"
          className="btn-live"
          style={{
            flex: '0 0 auto', width: 38, height: 38, borderRadius: 10, border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: !input.trim() || sending ? 'not-allowed' : 'pointer',
            background: !input.trim() || sending ? 'var(--surface-sunken)' : 'var(--ember)',
            color: !input.trim() || sending ? 'var(--text-tertiary)' : 'white',
          }}
        >
          <LucideIcon name="Send" size={16} />
        </button>
      </div>
    </div>
  );
};

export default AgentBetaChat;
